import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { authHeaders } from '../../services/auth';

const PHASE_OPTIONS = [
  { value: 'preclinical',   label: 'Preclinical' },
  { value: 'ind_filed',     label: 'IND Filed' },
  { value: 'phase1',        label: 'Phase 1' },
  { value: 'phase1b',       label: 'Phase 1b' },
  { value: 'phase1_2',      label: 'Phase 1/2' },
  { value: 'phase2',        label: 'Phase 2' },
  { value: 'phase2b',       label: 'Phase 2b' },
  { value: 'phase2_3',      label: 'Phase 2/3' },
  { value: 'phase3',        label: 'Phase 3' },
  { value: 'nda_bla_filed', label: 'NDA/BLA Filed' },
  { value: 'approved',      label: 'Approved' },
  { value: 'discontinued',  label: 'Discontinued' },
];

const MODALITY_OPTIONS = [
  { value: 'small_molecule',          label: 'Small Molecule' },
  { value: 'monoclonal_antibody',     label: 'Monoclonal Antibody' },
  { value: 'bispecific_antibody',     label: 'Bispecific Antibody' },
  { value: 'antibody_drug_conjugate', label: 'Antibody-Drug Conjugate' },
  { value: 'fusion_protein',          label: 'Fusion Protein' },
  { value: 'gene_therapy',            label: 'Gene Therapy' },
  { value: 'cell_therapy',            label: 'Cell Therapy' },
  { value: 'rna_therapy',             label: 'RNA Therapy' },
  { value: 'peptide',                 label: 'Peptide' },
  { value: 'protein',                 label: 'Protein' },
  { value: 'other',                   label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active',       label: 'Active' },
  { value: 'on_hold',      label: 'On Hold' },
  { value: 'discontinued', label: 'Discontinued' },
  { value: 'approved',     label: 'Approved' },
];

function getExistingAliases(program) {
  if (!program) return [];
  const dn = program.drug_names || program.aliases || [];
  return dn.filter(d => {
    const t = d.type || d.name_type;
    return typeof d === 'object' && t !== 'brand' && t !== 'inn';
  }).map(d => d.name).filter(Boolean);
}

function getExistingBrandName(program) {
  if (!program) return '';
  const dn = program.drug_names || program.aliases || [];
  const brand = dn.find(d => (d.type || d.name_type) === 'brand');
  return brand ? brand.name : (program.brand_name || '');
}

function getExistingIndicationIds(program) {
  if (!program) return [];
  if (program.indications && program.indications.length > 0) return program.indications.map(i => String(i.id));
  if (program.indication_id) return [String(program.indication_id)];
  return [];
}

function ProgramModal({ program, onClose, onSaved, onDeleted }) {
  const isEdit = !!program;

  const [form, setForm] = useState({
    drug_name:     program?.drug_name || '',
    company_id:    program?.company_id || '',
    current_phase: program?.current_phase || program?.phase || '',
    status:        program?.status || 'active',
    modality:      program?.modality || '',
    notes:         program?.notes || '',
  });

  const [selectedIndications, setSelectedIndications] = useState(getExistingIndicationIds(program));
  const [brandName,  setBrandName]  = useState(getExistingBrandName(program));
  const [aliasInput, setAliasInput] = useState('');
  const [aliases,    setAliases]    = useState(getExistingAliases(program));
  const [targetInput, setTargetInput] = useState('');
  const [targets,     setTargets]     = useState(() => {
    const all = [];
    if (program?.target) all.push(program.target);
    (program?.secondary_targets || []).forEach(t => { if (t && !all.includes(t)) all.push(t); });
    return all;
  });

  const [companies,      setCompanies]      = useState([]);
  const [indications,    setIndications]    = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [error,          setError]          = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [savingCompany,  setSavingCompany]  = useState(false);

  useEffect(() => {
    const reqs = [axios.get(`${API_BASE_URL}/companies`)];
    if (isEdit) reqs.push(axios.get(`${API_BASE_URL}/indications`));
    Promise.all(reqs).then(([c, i]) => {
      setCompanies(c.data?.companies || c.data || []);
      if (i) setIndications(i.data || []);
    }).catch(() => {});
  }, [isEdit]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleAddCompany() {
    const name = newCompanyName.trim();
    if (!name) return;
    setSavingCompany(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/companies`, { name }, { headers: authHeaders() });
      const created = res.data;
      setCompanies(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      handleChange('company_id', String(created.id));
      setNewCompanyName('');
      setShowAddCompany(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create company.');
    } finally {
      setSavingCompany(false);
    }
  }

  function addAlias() {
    const trimmed = aliasInput.trim();
    if (trimmed && !aliases.includes(trimmed)) setAliases(prev => [...prev, trimmed]);
    setAliasInput('');
  }

  function buildDrugNames() {
    const names = [];
    if (brandName.trim()) names.push({ name: brandName.trim(), name_type: 'brand', is_primary: false });
    aliases.forEach(a => names.push({ name: a, name_type: 'synonym', is_primary: false }));
    return names;
  }

  async function handleDelete() {
    if (!window.confirm(`Permanently delete ${program.drug_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/programs/${program.id}`, { headers: authHeaders() });
      onClose();
      if (onDeleted) onDeleted();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete program.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleSave() {
    const errors = [];
    if (!form.drug_name.trim()) errors.push('Drug name is required.');
    if (!form.company_id)       errors.push('Company is required.');
    if (errors.length) { setError(errors.join(' ')); return; }

    setSaving(true);
    setError(null);

    const payload = {
      ...form,
      current_phase:     form.current_phase || null,
      modality:          form.modality || null,
      target:            targets[0] || null,
      secondary_targets: targets.slice(1),
      indication_ids:    isEdit ? selectedIndications.map(id => parseInt(id, 10)) : [],
      notes:             form.notes.trim() || null,
      drug_names:        buildDrugNames(),
    };

    try {
      if (isEdit) {
        await axios.patch(`${API_BASE_URL}/programs/${program.id}`, payload, { headers: authHeaders() });
        onSaved(program.id);
      } else {
        const res = await axios.post(`${API_BASE_URL}/programs`, payload, { headers: authHeaders() });
        onSaved(res.data.id);
      }
      onClose();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Failed to save program.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? `Edit ${program.drug_name || 'Program'}` : 'Add Program'}
          </h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          <div className="form-grid">
            <div className="form-field">
              <label>Drug Name <span className="required-star">*</span></label>
              <input
                type="text"
                value={form.drug_name}
                onChange={e => handleChange('drug_name', e.target.value)}
                placeholder="e.g. upadacitinib"
                autoFocus
              />
            </div>
            <div className="form-field">
              <label>Brand Name</label>
              <input
                type="text"
                value={brandName}
                onChange={e => setBrandName(e.target.value)}
                placeholder="e.g. Rinvoq"
              />
            </div>
            <div className="form-field">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Company <span className="required-star">*</span></span>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: 11, padding: '2px 8px' }}
                  onClick={() => setShowAddCompany(v => !v)}
                >
                  {showAddCompany ? 'Cancel' : '+ New Company'}
                </button>
              </label>
              {showAddCompany ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={newCompanyName}
                    onChange={e => setNewCompanyName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCompany(); } }}
                    placeholder="Company name…"
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn-primary" style={{ fontSize: 12 }} onClick={handleAddCompany} disabled={savingCompany || !newCompanyName.trim()}>
                    {savingCompany ? '…' : 'Add'}
                  </button>
                </div>
              ) : (
                <select value={form.company_id} onChange={e => handleChange('company_id', e.target.value)}>
                  <option value="">Select company…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>

            {isEdit && (
              <>
                <div className="form-field">
                  <label>Phase</label>
                  <select value={form.current_phase} onChange={e => handleChange('current_phase', e.target.value)}>
                    <option value="">Select phase…</option>
                    {PHASE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select value={form.status} onChange={e => handleChange('status', e.target.value)}>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Indications</label>
                  <IndicationPicker
                    indications={indications}
                    selected={selectedIndications}
                    onChange={setSelectedIndications}
                    onNewIndication={created => setIndications(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))}
                  />
                </div>
              </>
            )}

            <div className="form-field">
              <label>Modality</label>
              <select value={form.modality} onChange={e => handleChange('modality', e.target.value)}>
                <option value="">Select modality…</option>
                {MODALITY_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Targets</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={targetInput}
                  onChange={e => setTargetInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const t = targetInput.trim();
                      if (t && !targets.includes(t)) setTargets(prev => [...prev, t]);
                      setTargetInput('');
                    }
                  }}
                  placeholder="e.g. JAK1 — press Enter to add"
                />
                <button type="button" className="btn-secondary" onClick={() => {
                  const t = targetInput.trim();
                  if (t && !targets.includes(t)) setTargets(prev => [...prev, t]);
                  setTargetInput('');
                }}>Add</button>
              </div>
              {targets.length > 0 && (
                <div className="alias-tags" style={{ marginTop: 8, padding: 0 }}>
                  {targets.map((t, i) => (
                    <span key={t} className="alias-tag" style={{ cursor: 'pointer', background: i === 0 ? '#eff6ff' : undefined, borderColor: i === 0 ? '#bfdbfe' : undefined }}>
                      {i === 0 && <span style={{ fontSize: 10, color: '#3b82f6', marginRight: 4 }}>PRIMARY</span>}
                      {t}
                      <span style={{ color: '#94a3b8', marginLeft: 4 }} onClick={() => setTargets(prev => prev.filter(x => x !== t))}>&times;</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-field" style={{ marginTop: 14 }}>
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              rows={2}
              placeholder="Any additional notes…"
            />
          </div>

          <div className="form-field" style={{ marginTop: 14 }}>
            <label>Drug Name Aliases / Development Codes</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={aliasInput}
                onChange={e => setAliasInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
                placeholder="e.g. ABT-494 — press Enter to add"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn-secondary" onClick={addAlias}>Add</button>
            </div>
            {aliases.length > 0 && (
              <div className="alias-tags" style={{ marginTop: 8, padding: 0 }}>
                {aliases.map(a => (
                  <span
                    key={a}
                    className="alias-tag"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setAliases(prev => prev.filter(x => x !== a))}
                    title="Click to remove"
                  >
                    {a} <span style={{ color: '#94a3b8', marginLeft: 4 }}>&times;</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div style={{ flex: 1 }}>
            {isEdit && (
              <button className="btn-danger" onClick={handleDelete} disabled={deleting || saving}>
                {deleting ? 'Deleting…' : 'Delete Program'}
              </button>
            )}
          </div>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || deleting}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Program'}
          </button>
        </div>
      </div>
    </div>
  );
}

function IndicationPicker({ indications, selected, onChange, onNewIndication }) {
  const [dropdownVal,  setDropdownVal]  = useState('');
  const [showNew,      setShowNew]      = useState(false);
  const [newName,      setNewName]      = useState('');
  const [savingNew,    setSavingNew]    = useState(false);

  const remaining = indications.filter(i => !selected.includes(String(i.id)));

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setSavingNew(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/indications`, { name });
      const created = res.data;
      onNewIndication(created);
      onChange([...selected, String(created.id)]);
      setNewName('');
      setShowNew(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create indication.');
    } finally {
      setSavingNew(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={dropdownVal}
          onChange={e => {
            const id = e.target.value;
            if (id && !selected.includes(id)) onChange([...selected, id]);
            setDropdownVal('');
          }}
          style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
        >
          <option value="">Add indication…</option>
          {remaining.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
        </select>
        <button
          type="button"
          className="btn-secondary"
          style={{ fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap' }}
          onClick={() => setShowNew(v => !v)}
        >
          {showNew ? 'Cancel' : '+ New'}
        </button>
      </div>
      {showNew && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }}
            placeholder="e.g. Pulmonary Fibrosis"
            autoFocus
            style={{ flex: 1 }}
          />
          <button type="button" className="btn-primary" style={{ fontSize: 12 }} onClick={handleCreate} disabled={savingNew || !newName.trim()}>
            {savingNew ? '…' : 'Add'}
          </button>
        </div>
      )}
      {selected.length > 0 && (
        <div className="alias-tags" style={{ marginTop: 8, padding: 0 }}>
          {selected.map((id, idx) => {
            const ind = indications.find(i => String(i.id) === id);
            return (
              <span key={id} className="alias-tag" style={{ cursor: 'pointer', background: idx === 0 ? '#eff6ff' : undefined, borderColor: idx === 0 ? '#bfdbfe' : undefined }}>
                {idx === 0 && <span style={{ fontSize: 10, color: '#3b82f6', marginRight: 4 }}>PRIMARY</span>}
                {ind?.name || id}
                <span style={{ color: '#94a3b8', marginLeft: 4 }} onClick={() => onChange(selected.filter(s => s !== id))}>&times;</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ProgramModal;
